/**
 * Survey edits as ops (one undo step each, synced like every other change), and the quick-add
 * point list of the survey screens.
 */
import {
  boardCircuits,
  newId,
  nextOrder,
  roomPoints,
  sheetsInOrder,
  surveyBoards,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type SurveyBoard,
  type SurveyCircuit,
  type SurveyPoint,
  type SurveyRoom,
} from '@elec/domain';
import { earthElectrodeOps, type BoardSpec } from '@elec/editor';

type L = { fr: string; en: string; nl: string };

export interface PointPreset {
  key: string;
  symbolId: string;
  props: Record<string, PropValue>;
  label: L;
  /** Shown first on the survey screen. */
  common?: boolean;
}

/** Points offered by the survey screens, most frequent first. */
export const POINT_PRESETS: PointPreset[] = [
  {
    key: 'socket',
    symbolId: 'iec.socket',
    props: {},
    label: { fr: 'Prise', en: 'Socket', nl: 'Stopcontact' },
    common: true,
  },
  {
    key: 'socket2',
    symbolId: 'iec.socket',
    props: { count: 2 },
    label: { fr: 'Prise double', en: 'Double socket', nl: 'Dubbel stopcontact' },
    common: true,
  },
  {
    key: 'light',
    symbolId: 'iec.light',
    props: {},
    label: { fr: 'Point lumineux', en: 'Light', nl: 'Lichtpunt' },
    common: true,
  },
  {
    key: 'switch',
    symbolId: 'iec.switch',
    props: {},
    label: { fr: 'Interrupteur', en: 'Switch', nl: 'Schakelaar' },
    common: true,
  },
  {
    key: 'twoway',
    symbolId: 'iec.switch-twoway',
    props: {},
    label: { fr: 'Va-et-vient', en: 'Two-way switch', nl: 'Wisselschakelaar' },
    common: true,
  },
  {
    key: 'wall',
    symbolId: 'iec.light-wall',
    props: {},
    label: { fr: 'Applique', en: 'Wall light', nl: 'Wandlamp' },
  },
  {
    key: 'pushbutton',
    symbolId: 'iec.pushbutton',
    props: {},
    label: { fr: 'Bouton-poussoir', en: 'Push button', nl: 'Drukknop' },
  },
  {
    key: 'motion',
    symbolId: 'iec.motion-detector',
    props: {},
    label: { fr: 'Détecteur', en: 'Motion detector', nl: 'Bewegingsmelder' },
  },
  {
    key: 'socket3p',
    symbolId: 'iec.socket-3p',
    props: {},
    label: { fr: 'Prise triphasée', en: '3-phase socket', nl: 'Driefasig stopcontact' },
  },
  {
    key: 'cooker',
    symbolId: 'iec.cooker',
    props: {},
    label: { fr: 'Taque / cuisinière', en: 'Cooker / hob', nl: 'Kookplaat / fornuis' },
  },
  { key: 'oven', symbolId: 'iec.oven', props: {}, label: { fr: 'Four', en: 'Oven', nl: 'Oven' } },
  {
    key: 'dishwasher',
    symbolId: 'iec.dishwasher',
    props: {},
    label: { fr: 'Lave-vaisselle', en: 'Dishwasher', nl: 'Vaatwasser' },
  },
  {
    key: 'washer',
    symbolId: 'iec.washing-machine',
    props: {},
    label: { fr: 'Lave-linge', en: 'Washing machine', nl: 'Wasmachine' },
  },
  {
    key: 'dryer',
    symbolId: 'iec.dryer',
    props: {},
    label: { fr: 'Sèche-linge', en: 'Tumble dryer', nl: 'Droogkast' },
  },
  {
    key: 'boiler',
    symbolId: 'iec.water-heater',
    props: {},
    label: { fr: 'Chauffe-eau', en: 'Water heater', nl: 'Boiler' },
  },
  {
    key: 'heater',
    symbolId: 'iec.heater',
    props: {},
    label: { fr: 'Radiateur', en: 'Electric heater', nl: 'Elektrische verwarming' },
  },
  {
    key: 'heatpump',
    symbolId: 'iec.heat-pump',
    props: {},
    label: { fr: 'Pompe à chaleur', en: 'Heat pump', nl: 'Warmtepomp' },
  },
  {
    key: 'ev',
    symbolId: 'iec.ev-charger',
    props: {},
    label: { fr: 'Borne VE', en: 'EV charger', nl: 'Laadpaal' },
  },
  {
    key: 'ventilation',
    symbolId: 'iec.ventilation',
    props: {},
    label: { fr: 'Ventilation', en: 'Ventilation', nl: 'Ventilatie' },
  },
  {
    key: 'appliance',
    symbolId: 'iec.appliance',
    props: {},
    label: { fr: 'Appareil fixe', en: 'Fixed appliance', nl: 'Vast toestel' },
  },
];

const sameProps = (a: Record<string, PropValue>, b: Record<string, PropValue>) =>
  Object.keys(a).length === Object.keys(b).length && Object.entries(a).every(([k, v]) => b[k] === v);

/** The preset a point line was made from (for its name and icon). */
export const presetOf = (p: Pick<SurveyPoint, 'symbolId' | 'props'>): PointPreset | undefined =>
  POINT_PRESETS.find((x) => x.symbolId === p.symbolId && sameProps(x.props, p.props)) ??
  POINT_PRESETS.find((x) => x.symbolId === p.symbolId);

export function addRoomOp(
  project: Project,
  init: Partial<Omit<SurveyRoom, 'id'>> = {},
): { op: 'add'; coll: 'surveyRooms'; value: SurveyRoom } {
  const last = Object.values(project.surveyRooms).sort((a, b) => b.order - a.order)[0];
  return {
    op: 'add',
    coll: 'surveyRooms',
    value: {
      id: newId(),
      name: '',
      roomType: 'other',
      level: last?.level ?? '',
      order: nextOrder(project.surveyRooms),
      notes: '',
      photos: [],
      ...init,
    },
  };
}

export function addPointOp(
  project: Project,
  roomId: Id,
  preset: Pick<PointPreset, 'symbolId' | 'props'>,
  init: Partial<Omit<SurveyPoint, 'id' | 'roomId'>> = {},
): { op: 'add'; coll: 'surveyPoints'; value: SurveyPoint } {
  return {
    op: 'add',
    coll: 'surveyPoints',
    value: {
      id: newId(),
      roomId,
      symbolId: preset.symbolId,
      props: { ...preset.props },
      quantity: 1,
      order: nextOrder(Object.fromEntries(roomPoints(project, roomId).map((p) => [p.id, p]))),
      notes: '',
      ...init,
    },
  };
}

export function addBoardOp(
  project: Project,
  init: Partial<Omit<SurveyBoard, 'id'>> = {},
): { op: 'add'; coll: 'surveyBoards'; value: SurveyBoard } {
  return {
    op: 'add',
    coll: 'surveyBoards',
    value: {
      id: newId(),
      name: '',
      order: nextOrder(project.surveyBoards),
      photos: [],
      rcds: [],
      notes: '',
      ...init,
    },
  };
}

export function addCircuitOp(
  project: Project,
  boardId: Id,
  init: Partial<Omit<SurveyCircuit, 'id' | 'boardId'>> = {},
): { op: 'add'; coll: 'surveyCircuits'; value: SurveyCircuit } {
  return {
    op: 'add',
    coll: 'surveyCircuits',
    value: {
      id: newId(),
      boardId,
      order: nextOrder(project.surveyCircuits),
      label: '',
      device: 'MCB',
      legacy: false,
      notes: '',
      ...init,
    },
  };
}

/** Remove a room and its points. */
export function removeRoomOps(project: Project, roomId: Id): Op[] {
  return [
    ...roomPoints(project, roomId).map((p): Op => ({ op: 'remove', coll: 'surveyPoints', id: p.id })),
    { op: 'remove', coll: 'surveyRooms', id: roomId },
  ];
}

/** Remove a circuit; its points stay, without a circuit. */
export function removeCircuitOps(project: Project, circuitId: Id): Op[] {
  const ops: Op[] = [];
  for (const p of Object.values(project.surveyPoints))
    if (p.circuitId === circuitId)
      ops.push({ op: 'update', coll: 'surveyPoints', id: p.id, patch: { circuitId: undefined } });
  ops.push({ op: 'remove', coll: 'surveyCircuits', id: circuitId });
  return ops;
}

/** Remove a board and its circuits. */
export function removeBoardOps(project: Project, boardId: Id): Op[] {
  return [
    ...boardCircuits(project, boardId).flatMap((c) => removeCircuitOps(project, c.id)),
    { op: 'remove', coll: 'surveyBoards', id: boardId },
  ];
}

/** Remove an RCD from a board: its circuits move to the main bus. */
export function removeRcdOps(project: Project, boardId: Id, key: string): Op[] {
  const board = project.surveyBoards[boardId];
  if (!board) return [];
  const ops: Op[] = [];
  for (const c of boardCircuits(project, boardId))
    if (c.rcdKey === key)
      ops.push({ op: 'update', coll: 'surveyCircuits', id: c.id, patch: { rcdKey: undefined } });
  ops.push({
    op: 'update',
    coll: 'surveyBoards',
    id: boardId,
    patch: { rcds: board.rcds.filter((r) => r.key !== key) },
  });
  return ops;
}

/** A key not used by the board's RCDs yet. */
export const nextRcdKey = (board: SurveyBoard) => {
  for (let i = 1; ; i++) if (!board.rcds.some((r) => r.key === `r${i}`)) return `r${i}`;
};

/** Move an item one place up or down among `siblings` (swaps the `order` values). */
export function moveOps(
  coll: 'surveyRooms' | 'surveyPoints' | 'surveyCircuits' | 'surveyBoards',
  siblings: Array<{ id: Id; order: number }>,
  id: Id,
  delta: -1 | 1,
): Op[] {
  const i = siblings.findIndex((s) => s.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= siblings.length) return [];
  const a = siblings[i]!;
  const b = siblings[j]!;
  // Equal orders (concurrent adds): spread them first.
  const oa = a.order === b.order ? b.order + delta : b.order;
  return [
    { op: 'update', coll, id: a.id, patch: { order: oa } },
    { op: 'update', coll, id: b.id, patch: { order: a.order } },
  ] as Op[];
}

const num = (v: PropValue | undefined) => (typeof v === 'number' ? v : undefined);

/**
 * Fill in a board from a board structure read by the AI (photo of the board) or a sketch:
 * main switch, main RCD, RCDs and circuits. Circuits already recorded are updated in board
 * order and keep their points; extra ones are added.
 */
export function applyBoardSpecOps(project: Project, boardId: Id, spec: BoardSpec): Op[] {
  const board = project.surveyBoards[boardId];
  if (!board) return [];
  const ops: Op[] = [];
  const rcds = spec.groups
    .filter((g) => g.rcd)
    .map((g, i) => ({
      key: `r${i + 1}`,
      sensitivityMa: num(g.rcd!.sensitivityMa) ?? 30,
      rcdType: (['AC', 'A', 'F', 'B'].includes(String(g.rcd!.rcdType)) ? g.rcd!.rcdType : 'A') as
        'AC' | 'A' | 'F' | 'B',
      ratingA: num(g.rcd!.ratingA) ?? 40,
    }));
  ops.push({
    op: 'update',
    coll: 'surveyBoards',
    id: boardId,
    patch: {
      main: spec.main ? { ...spec.main } : undefined,
      mainRcd: spec.mainRcd ? { ...spec.mainRcd } : undefined,
      rcds,
    },
  });
  const existing = boardCircuits(project, boardId);
  let rcdIndex = 0;
  let n = 0;
  let order = nextOrder(project.surveyCircuits);
  for (const g of spec.groups) {
    const rcdKey = g.rcd ? `r${++rcdIndex}` : undefined;
    for (const c of g.circuits) {
      const device: SurveyCircuit['device'] =
        c.device.symbolId === 'iec.rcbo' ? 'RCBO' : c.device.symbolId === 'iec.fuse' ? 'fuse' : 'MCB';
      const p = c.device.props ?? {};
      const curve = ['B', 'C', 'D'].includes(String(p.curve)) ? (p.curve as 'B' | 'C' | 'D') : undefined;
      const fields = {
        device,
        ratingA: num(p.ratingA),
        curve,
        poles: num(p.poles),
        rcdKey,
        ...(c.cable
          ? { cable: { type: c.cable.type, sectionMm2: c.cable.sectionMm2, cores: c.cable.cores } }
          : {}),
      };
      const old = existing[n++];
      if (old) {
        ops.push({
          op: 'update',
          coll: 'surveyCircuits',
          id: old.id,
          patch: { ...fields, ...(old.label ? {} : { label: c.label }) },
        });
      } else ops.push(addCircuitOp(project, boardId, { ...fields, label: c.label, order: order++ }));
    }
  }
  // Circuits the photo did not show keep their place on the main bus.
  for (const old of existing.slice(n))
    if (old.rcdKey)
      ops.push({ op: 'update', coll: 'surveyCircuits', id: old.id, patch: { rcdKey: undefined } });
  return ops;
}

/** The default board of a survey (the first), if any. */
export const firstBoard = (project: Project): SurveyBoard | undefined => surveyBoards(project)[0];

/**
 * Set the measured earth resistance everywhere it is kept: on the survey board (so generating
 * the diagrams again keeps it) and on the diagram's earth electrode. Without a survey, an
 * electrode is drawn beside the supply when the diagram has none; with one, the generator draws
 * it. `value` undefined clears it.
 */
export function earthResistanceOps(project: Project, value: number | undefined, boardId?: Id): Op[] {
  const boards = surveyBoards(project);
  const board =
    (boardId && boards.find((b) => b.id === boardId)) ??
    boards.find((b) => b.earthOhm !== undefined) ??
    boards[0];
  const ops: Op[] = [];
  if (board && board.earthOhm !== value)
    ops.push({ op: 'update', coll: 'surveyBoards', id: board.id, patch: { earthOhm: value } });
  const singleLine = sheetsInOrder(project).find((s) => s.kind === 'single-line');
  const drawn = Object.values(project.elements).some((e) => e.symbolId === 'iec.earth-electrode');
  if (drawn || !boards.length) ops.push(...earthElectrodeOps(project, singleLine?.id, value ?? null).ops);
  return ops;
}
